// pages/dashboard/DashboardHome.jsx
// Steve's main dashboard with metrics and recent reports

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const DashboardHome = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [feedbackTotal, setFeedbackTotal] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const reportsRes = await axios.get('/reports?limit=10');
      if (reportsRes.data.success) {
        const reports = reportsRes.data.data.reports;
        const total = reportsRes.data.data.total;
        const counts = reportsRes.data.data.statusCounts;
        setRecentReports(reports);
        setMetrics({
          totalReports: total,
          // Counts come from the server so they cover every report, not just
          // the 10 fetched for the Recent Reports list. The filter below is a
          // fallback for a server that has not been updated yet.
          pendingReview: counts
            ? counts.pending_review || 0
            : reports.filter(r => r.paidReport?.status === 'pending_review').length,
          sentReports: counts
            ? counts.sent || 0
            : reports.filter(r => r.paidReport?.status === 'sent').length,
        });
      }
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }

    // Feedback responses were only visible inside Form Maker. Fetched
    // separately so a failure here cannot blank the whole dashboard.
    try {
      const fbRes = await axios.get('/feedback/responses?limit=1');
      if (fbRes.data.success) {
        setFeedbackTotal(fbRes.data.data.total || 0);
      }
    } catch (err) {
      console.error('Feedback count error:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const getStatusBadge = (status) => {
    const map = {
      pending_review: { bg: '#FEF9E7', color: '#A04000', label: 'Pending Review' },
      generating: { bg: '#EBF5FB', color: '#1A5276', label: 'Generating' },
      approved: { bg: '#E8F8EF', color: '#1E8449', label: 'Approved' },
      sent: { bg: '#E8F8EF', color: '#1E8449', label: 'Sent' },
    };
    const b = map[status] || { bg: '#F4F6F9', color: '#7F8C8D', label: status || 'Unknown' };
    return <span style={{ backgroundColor: b.bg, color: b.color, padding: '3px 10px', borderRadius: '100px', fontSize: '12px', fontWeight: '500' }}>{b.label}</span>;
  };

  const navItems = [
    { label: 'Dashboard', path: '/admin/dashboard', icon: '📊', active: true },
    { label: 'Submissions', path: '/admin/submissions', icon: '📋' },
    { label: 'Transactions', path: '/admin/transactions', icon: '💳' },
    { label: 'Meetings', path: '/admin/meetings', icon: '📅' },
    { label: 'Enterprise', path: '/admin/enterprise', icon: '🏢' },
    { label: 'Form Maker', path: '/admin/forms', icon: '📝' },
    { label: 'Analytics', path: '/admin/analytics', icon: '📈' },
    { label: 'Settings', path: '/admin/settings', icon: '⚙️' },
  ];

  return (
    <div style={s.page}>
      <div style={s.sidebar}>
        <div style={s.sidebarLogo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
        <nav style={s.nav}>
          {navItems.map(item => (
            <button key={item.path} style={{ ...s.navItem, ...(item.active ? s.navActive : {}) }} onClick={() => navigate(item.path)}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={s.sidebarFooter}>
          <div style={s.adminName}>{admin?.name}</div>
          <div style={s.adminEmail}>{admin?.email}</div>
          <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Dashboard</h1>
            <p style={s.subtitle}>Welcome back, {admin?.name}</p>
          </div>
          <button style={s.btnPrimary} onClick={() => navigate('/admin/submissions')}>View All Submissions</button>
        </div>

        {/* Big action card for Steve: see pending reviews at a glance */}
        {!loading && (
          (metrics?.pendingReview || 0) > 0 ? (
            <div style={s.heroCard} onClick={() => navigate('/admin/submissions')}>
              <div>
                <div style={s.heroCount}>{metrics.pendingReview}</div>
                <div style={s.heroLabel}>{metrics.pendingReview === 1 ? 'report is waiting for your review' : 'reports are waiting for your review'}</div>
              </div>
              <button style={s.heroBtn} onClick={(e) => { e.stopPropagation(); navigate('/admin/submissions'); }}>
                Review Now →
              </button>
            </div>
          ) : (
            <div style={s.heroCardDone}>
              <span style={s.heroDoneIcon}>✅</span>
              <span style={s.heroDoneText}>All caught up. No reports waiting for review.</span>
            </div>
          )
        )}

        <div style={s.metricsGrid}>
          {[
            { label: 'Total Assessments', value: metrics?.totalReports || 0, icon: '📋', color: '#1B9AAA' },
            { label: 'Pending Your Review', value: metrics?.pendingReview || 0, icon: '⏳', color: '#F39C12', urgent: (metrics?.pendingReview || 0) > 0 },
            { label: 'Reports Sent', value: metrics?.sentReports || 0, icon: '✅', color: '#27AE60' },
            { label: 'Feedback Responses', value: feedbackTotal ?? 0, icon: '💬', color: '#8E44AD', detailPath: '/admin/submissions?tab=feedback' },
          ].map((m, i) => (
            <div key={i} style={{ ...s.metricCard, ...(m.urgent ? s.metricUrgent : {}) }}>
              <div style={s.metricIcon}>{m.icon}</div>
              <div style={{ ...s.metricValue, color: m.color }}>{loading ? '...' : m.value}</div>
              <div style={s.metricLabel}>{m.label}</div>
              {m.urgent && <div style={s.urgentTag}>Action Required</div>}
              {m.detailPath && (
                <button style={s.metricDetailBtn} onClick={() => navigate(m.detailPath)}>
                  View Detail →
                </button>
              )}
            </div>
          ))}
        </div>

        <div style={s.card}>
          <div style={s.cardHead}>
            <h2 style={s.cardTitle}>Recent Reports</h2>
            <button style={s.btnLink} onClick={() => navigate('/admin/submissions')}>View all →</button>
          </div>
          {loading ? (
            <p style={s.loadingText}>Loading...</p>
          ) : recentReports.length === 0 ? (
            <div style={s.empty}>
              <p>No reports yet. They will appear here after users complete assessments.</p>
            </div>
          ) : (
            <div>
              <div style={s.tableHead}>
                <span>Name</span><span>Company</span><span>Type</span><span>Status</span><span>Date</span><span>Action</span>
              </div>
              {recentReports.map(report => (
                <div key={report._id} style={s.tableRow}>
                  <span style={s.name}>{report.userId?.firstName} {report.userId?.lastName}</span>
                  <span style={s.cell}>{report.userId?.companyName || 'N/A'}</span>
                  <span style={s.cell}>
                    <span style={{ ...s.typeBadge, ...(report.userId?.userType === 'enterprise' ? s.typeEnt : s.typeInd) }}>
                      {report.userId?.userType || 'individual'}
                    </span>
                  </span>
                  <span style={s.cell}>{getStatusBadge(report.paidReport?.status)}</span>
                  <span style={s.cell}>{new Date(report.createdAt).toLocaleDateString()}</span>
                  <span style={s.cell}>
                    <button style={s.btnReview} onClick={() => navigate(`/admin/submissions/${report._id}`)}>
                      {report.paidReport?.status === 'pending_review' ? '⚡ Review' : 'View'}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-primary)' },
  sidebar: { width: '240px', backgroundColor: 'var(--color-primary)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarLogo: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  nav: { flex: 1, padding: '16px 0' },
  navItem: { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer', textAlign: 'left' },
  navActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  adminName: { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '2px' },
  adminEmail: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' },
  logoutBtn: { width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer' },
  main: { flex: 1, backgroundColor: 'var(--color-dashboard-bg)', padding: '32px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' },
  title: { fontSize: '26px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: 'var(--color-text-light)' },
  btnPrimary: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '10px 20px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  heroCard: { backgroundColor: '#FFF6E5', border: '2px solid #F39C12', borderRadius: '14px', padding: '24px 28px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxShadow: 'var(--shadow-sm)' },
  heroCount: { fontSize: '44px', fontWeight: '700', color: '#A04000', lineHeight: '1' },
  heroLabel: { fontSize: '16px', color: '#7D6608', marginTop: '6px', fontWeight: '500' },
  heroBtn: { backgroundColor: '#F39C12', color: '#fff', padding: '16px 32px', borderRadius: '10px', border: 'none', fontSize: '17px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' },
  heroCardDone: { backgroundColor: '#E8F8EF', border: '1px solid #27AE60', borderRadius: '14px', padding: '18px 24px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' },
  heroDoneIcon: { fontSize: '20px' },
  heroDoneText: { fontSize: '15px', color: '#1E8449', fontWeight: '500' },
  metricsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' },
  metricDetailBtn: { marginTop: '12px', backgroundColor: 'transparent', border: 'none', padding: 0, color: 'var(--color-teal)', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  metricCard: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '24px', boxShadow: 'var(--shadow-sm)' },
  metricUrgent: { border: '2px solid #F39C12', backgroundColor: '#FFFBF0' },
  metricIcon: { fontSize: '28px', marginBottom: '8px' },
  metricValue: { fontSize: '36px', fontWeight: '700', lineHeight: 1, marginBottom: '4px' },
  metricLabel: { fontSize: '13px', color: 'var(--color-text-light)' },
  urgentTag: { display: 'inline-block', backgroundColor: '#FEF9E7', color: '#A04000', fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '100px', marginTop: '8px' },
  card: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)', padding: '24px' },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  cardTitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)' },
  btnLink: { backgroundColor: 'transparent', border: 'none', color: 'var(--color-teal)', fontSize: '14px', cursor: 'pointer', fontWeight: '500' },
  loadingText: { padding: '20px', textAlign: 'center', color: 'var(--color-text-light)', fontSize: '14px' },
  empty: { padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-light)', fontSize: '14px' },
  tableHead: { display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.8fr 1fr 0.8fr 0.6fr', gap: '12px', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid var(--color-border)', marginBottom: '4px' },
  tableRow: { display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.8fr 1fr 0.8fr 0.6fr', gap: '12px', padding: '12px', borderRadius: '8px', backgroundColor: 'var(--color-off-white)', alignItems: 'center', marginBottom: '4px' },
  name: { fontSize: '14px', fontWeight: '600', color: 'var(--color-primary)' },
  cell: { fontSize: '13px', color: 'var(--color-text-light)' },
  typeBadge: { padding: '3px 8px', borderRadius: '100px', fontSize: '11px', fontWeight: '500' },
  typeInd: { backgroundColor: '#EBF5FB', color: '#1A5276' },
  typeEnt: { backgroundColor: '#E8F8EF', color: '#1E8449' },
  btnReview: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '12px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' },
};

export default DashboardHome;
